import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from '../../../ui/toast';
import { educationSummary } from '../utils/bankSearchAdapters';
import {
  BuildingIcons,
  ClockIcons,
  EmailIcons,
  EyeIcons,
  FilePlusIcons,
  MapPinIcons,
  PhoneIcons,
  StarIcons,
  XIcons,
} from "../../../icons/icons";
import { apiUrl } from "../../../utils/api_service/api_url";
import { PDFDisplay } from "../../job_openings/create/ApplicantAnalysis/PDFDisplay";
import { CandidateDocument, CandidateType } from "../constants/mockData";
import { StatusHistoryTimeline } from "../../../ui/components/Timeline";

interface CandidateProfileModalProps {
  candidate: CandidateType | null;
  isOpen: boolean;
  onClose: () => void;
  getStatusColor: (status: string) => string;
  getStatusText: (status: string) => string;
  getMatchColor: (percentage: number) => string;
  onInvite?: (candidate: CandidateType) => void;
}

export const CandidateProfileModal: React.FC<CandidateProfileModalProps> = ({
  candidate,
  isOpen,
  onClose,
  getStatusColor,
  getStatusText,
  getMatchColor,
  onInvite,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<"profile" | "cv" | "documents">("profile");
  const [selectedDocument, setSelectedDocument] = useState<CandidateDocument | null>(null);
  const [docPreviewUrl, setDocPreviewUrl] = useState<string | null>(null);
  const [isDocLoading, setIsDocLoading] = useState<boolean>(false);

  // Synthesize list of documents from candidate.documents or candidate.filename
  const effectiveDocuments: CandidateDocument[] = React.useMemo(() => {
    if (!candidate) return [];
    const raw = candidate as any;
    if (candidate.documents && candidate.documents.length > 0) {
      return candidate.documents;
    }
    if (raw.applicant_documents && raw.applicant_documents.length > 0) {
      return raw.applicant_documents;
    }
    if (raw.applicantDocuments && raw.applicantDocuments.length > 0) {
      return raw.applicantDocuments;
    }

    const filename =
      candidate.filename ||
      raw.fileName ||
      raw.s3Filename ||
      raw.s3_filename ||
      candidate.pdfUrl ||
      raw.pdf_url ||
      raw.file_path ||
      raw.filePath ||
      raw.cv_filename ||
      raw.url;

    if (filename && typeof filename === "string") {
      const baseName = filename.split("/").pop() || "Resume.pdf";
      return [
        {
          id: "main-resume",
          s3Filename: filename,
          baseFilename: baseName,
          requirementKey: "resume",
          requirementLabel: "Main CV / Resume",
        },
      ];
    }
    return [];
  }, [candidate]);

  // Set default selected document when candidate changes
  useEffect(() => {
    if (effectiveDocuments.length > 0) {
      const resume = effectiveDocuments.find((d) => d.requirementKey === "resume") || effectiveDocuments[0];
      setSelectedDocument(resume);
    } else {
      setSelectedDocument(null);
    }
  }, [effectiveDocuments]);

  // Fetch document preview URL from backend storage API if s3Filename is provided
  useEffect(() => {
    let isCancelled = false;
    let createdBlobUrl: string | null = null;

    const resolveUrl = async () => {
      if (!candidate) {
        setDocPreviewUrl(null);
        setIsDocLoading(false);
        return;
      }

      setIsDocLoading(true);
      const raw = candidate as any;
      let targetFilename =
        selectedDocument?.s3Filename ||
        candidate?.filename ||
        raw?.fileName ||
        raw?.s3Filename ||
        raw?.s3_filename ||
        candidate?.pdfUrl ||
        raw?.pdf_url ||
        raw?.file_path ||
        raw?.filePath ||
        raw?.cv_filename ||
        raw?.url;

      // If targetFilename is missing, fetch full applicant object from 2d-graph for candidate.id
      if (!targetFilename && candidate.id) {
        try {
          const authToken = localStorage.getItem("token") || localStorage.getItem("adminToken") || "";
          const graphResp = await fetch(`${apiUrl}/job-applicant/2d-graph`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify({
              additional_skills: { skill_weightage: false, similar: false, skills: [] },
              job_title: { similar: false, job_titles: [] },
              keywords: { keyword_weightage: false, similar: false, keywords: [] },
              behavioral_attributes: { similar: false, behavioral_attributes: [] },
              gender: [],
              age: { min_age: 0, max_age: 100, include_unknown: true },
              applicant_status: [],
              candidate_ids: [candidate.id],
            }),
          });

          if (graphResp.ok) {
            const graphData = await graphResp.json();
            const found = graphData.data?.find((d: any) => d.id === candidate.id || d.userId === candidate.id) || graphData.data?.[0];
            if (found) {
              targetFilename =
                found.filename ||
                found.fileName ||
                found.s3Filename ||
                found.s3_filename ||
                found.pdfUrl ||
                found.pdf_url ||
                found.file_path ||
                found.filePath ||
                found.cv_filename;
            }
          }
        } catch (e) {
          console.error("Error fetching candidate detail from 2d-graph:", e);
        }
      }

      if (!targetFilename || typeof targetFilename !== "string") {
        setDocPreviewUrl(null);
        setIsDocLoading(false);
        return;
      }

      if (targetFilename.startsWith("http://") || targetFilename.startsWith("https://") || targetFilename.startsWith("blob:")) {
        setDocPreviewUrl(targetFilename);
        setIsDocLoading(false);
        return;
      }

      setIsDocLoading(true);
      try {
        const url = `${apiUrl}/storage?file_path=${encodeURIComponent(targetFilename)}`;
        const authToken = localStorage.getItem("token") || localStorage.getItem("adminToken") || "";

        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authToken}`,
            Accept: "application/json",
          },
        });

        if (response.ok && !isCancelled) {
          const contentType = response.headers.get("content-type");
          let docUrl = "";

          if (contentType && contentType.includes("application/json")) {
            const data = await response.json();
            docUrl = typeof data === "string" ? data : data?.url || data?.signedUrl || data?.downloadUrl || data?.fileUrl || url;
          } else {
            docUrl = response.url || url;
          }

          if (docUrl && !isCancelled) {
            const filenameLower = targetFilename.toLowerCase();
            if (filenameLower.endsWith(".pdf")) {
              try {
                const s3Response = await fetch(docUrl);
                if (s3Response.ok && !isCancelled) {
                  const blob = await s3Response.blob();
                  const previewBlob = new Blob([blob], { type: "application/pdf" });
                  createdBlobUrl = URL.createObjectURL(previewBlob);
                  setDocPreviewUrl(createdBlobUrl);
                } else if (!isCancelled) {
                  setDocPreviewUrl(docUrl);
                }
              } catch {
                if (!isCancelled) setDocPreviewUrl(docUrl);
              }
            } else {
              setDocPreviewUrl(docUrl);
            }
          }
        } else if (!isCancelled) {
          setDocPreviewUrl(`${apiUrl}/storage?file_path=${encodeURIComponent(targetFilename)}`);
        }
      } catch (err) {
        console.error("Error fetching document storage link:", err);
        if (!isCancelled) {
          setDocPreviewUrl(`${apiUrl}/storage?file_path=${encodeURIComponent(targetFilename)}`);
        }
      } finally {
        if (!isCancelled) {
          setIsDocLoading(false);
        }
      }
    };

    resolveUrl();

    return () => {
      isCancelled = true;
      if (createdBlobUrl) {
        URL.revokeObjectURL(createdBlobUrl);
      }
    };
  }, [selectedDocument, candidate]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
      setIsExpanded(false);
      setActiveTab("profile");
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  if (!candidate || !isOpen) return null;

  const scorePercent = candidate.score == null ? null : Math.round(candidate.score * 100);
  const statusLabel = getStatusText(candidate.status) || candidate.status;
  const statusClasses = getStatusColor(candidate.status) || "text-slate-600 bg-slate-100";

  const formatDate = (date?: string) => {
    if (!date) return "Present";
    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getEducationSummary = () => educationSummary(candidate.educations);

  // Download CV Handler — requests signed URL from /storage using candidate profile file_path
  const handleDownloadCV = async () => {
    const raw = candidate as any;

    // Resolve the file path of the opened candidate profile
    const filePath =
      selectedDocument?.s3Filename ||
      candidate?.filename ||
      raw?.fileName ||
      raw?.s3Filename ||
      raw?.s3_filename ||
      candidate?.pdfUrl ||
      raw?.pdf_url ||
      raw?.file_path ||
      raw?.filePath ||
      raw?.cv_filename ||
      raw?.url;

    if (!filePath) {
      toast.warn("No CV document available for download.");
      return;
    }

    const toastId = toast.info("Preparing download...", { autoClose: false });

    try {
      let downloadUrl = "";

      // If filePath is already a full external URL, use it directly
      if (filePath.startsWith("http://") || filePath.startsWith("https://")) {
        downloadUrl = filePath;
      } else {
        // Send the file path of opened candidate profile to /storage
        const authToken = localStorage.getItem("token") || localStorage.getItem("adminToken") || "";
        const storageUrl = `${apiUrl}/storage?file_path=${encodeURIComponent(filePath)}`;

        const response = await fetch(storageUrl, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authToken}`,
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Storage API error: ${response.status}`);
        }

        const data = await response.json();
        downloadUrl = typeof data === "string" ? data : data?.url || data?.signedUrl || data?.downloadUrl || data?.fileUrl;
      }

      if (!downloadUrl) {
        throw new Error("No downloadable URL found in storage API response");
      }

      // Determine extension and clean candidate filename
      const cleanPath = (filePath || downloadUrl).split("?")[0];
      const ext = cleanPath.split(".").pop()?.toLowerCase();
      const validExtension = ext && ["pdf", "doc", "docx", "png", "jpg", "jpeg"].includes(ext) ? ext : "pdf";
      const candidateName = (candidate?.name || "Candidate")
        .trim()
        .replace(/[^a-zA-Z0-9_\-\s]/g, "")
        .replace(/\s+/g, "_");
      const downloadFilename = `${candidateName}_CV.${validExtension}`;

      let downloaded = false;
      try {
        const fileResponse = await fetch(downloadUrl);
        if (fileResponse.ok) {
          const blob = await fileResponse.blob();
          if (blob && blob.size > 0) {
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = downloadFilename;
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
              document.body.removeChild(link);
              window.URL.revokeObjectURL(blobUrl);
            }, 3000);
            downloaded = true;
          }
        }
      } catch {
        // Direct fetch failed (e.g. CORS), fallback to link click
      }

      if (!downloaded) {
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = downloadFilename;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
        }, 1000);
      }

      toast.dismiss(toastId);
      toast.success("Download started!");
    } catch (error) {
      console.error("CV download error:", error);
      toast.dismiss(toastId);
      toast.error("Download failed. Please try again.");
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4 bg-slate-950/60 backdrop-blur-sm">
      <div
        className={`relative w-full transition-all duration-300 overflow-hidden rounded-2xl bg-white shadow-[0_32px_80px_rgba(15,23,42,0.18)] flex flex-col ${
          isExpanded
            ? "max-w-[98vw] h-[95vh]"
            : "max-w-[1300px] h-[90vh]"
        }`}
      >
        {/* Header Bar */}
        <div className="flex flex-col gap-3 border-b border-slate-200 bg-slate-50/90 px-4 sm:px-6 py-3.5 sm:flex-row sm:items-center sm:justify-between flex-shrink-0">
          {/* Left: Avatar + Profile details + Match Score */}
          <div className="flex items-center gap-3.5 min-w-0">
            <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-slate-900 text-sm font-bold text-white shadow-sm overflow-hidden">
              {candidate.avatar ? (
                <img
                  src={candidate.avatar}
                  alt={candidate.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                candidate.name
                  .split(" ")
                  .map((part) => part[0])
                  .join("")
                  .slice(0, 2)
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-400">
                  Candidate Profile
                </p>
              </div>
              <div className="flex items-center gap-2.5 mt-0.5 flex-wrap">
                <h1 className="truncate text-base sm:text-lg font-bold text-slate-900">
                  {candidate.name}
                </h1>
                {scorePercent != null && (
                  <div
                    className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold text-white shadow-xs ${getMatchColor(
                      scorePercent
                    )}`}
                  >
                    <span>{scorePercent}% Match</span>
                  </div>
                )}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-600">
                <span className="inline-flex items-center gap-1 rounded-md bg-white border border-slate-200/80 px-2 py-0.5 font-medium text-slate-600">
                  <MapPinIcons className="h-3 w-3 text-slate-400" />
                  {candidate.location ?? "Location unknown"}
                </span>
                <span className="inline-flex items-center gap-1 rounded-md bg-white border border-slate-200/80 px-2 py-0.5 font-medium text-slate-600">
                  <ClockIcons className="h-3 w-3 text-slate-400" />
                  {Math.floor(candidate.experience)} yrs exp
                </span>
                <span
                  className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-semibold ${statusClasses}`}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-current" />
                  {statusLabel}
                </span>
              </div>
            </div>
          </div>

          {/* Right: Actions + Close Button */}
          <div className="flex items-center gap-2 justify-end flex-shrink-0 flex-wrap">
            {/* Mobile Tab Selector Pill */}
            <div className="flex lg:hidden rounded-lg bg-slate-200 p-0.5 text-xs font-semibold">
              <button
                type="button"
                onClick={() => setActiveTab("profile")}
                className={`px-3 py-1 rounded-md transition-all ${
                  activeTab === "profile" ? "bg-white text-slate-900 shadow-xs font-bold" : "text-slate-600"
                }`}
              >
                Profile
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("cv")}
                className={`px-3 py-1 rounded-md transition-all ${
                  activeTab === "cv" ? "bg-emerald-600 text-white shadow-xs font-bold" : "text-slate-600"
                }`}
              >
                CV Viewer
              </button>
            </div>

            <button
              type="button"
              onClick={() => setIsExpanded((prev) => !prev)}
              className={`hidden sm:inline-flex items-center justify-center rounded-xl px-3.5 py-1.5 text-xs font-bold transition shadow-xs ${
                isExpanded
                  ? "bg-slate-800 text-white hover:bg-slate-700"
                  : "bg-slate-900 text-white hover:bg-slate-800"
              }`}
            >
              <EyeIcons className="mr-1.5 h-3.5 w-3.5" />{" "}
              {isExpanded ? "Collapse" : "Full View"}
            </button>

            {docPreviewUrl && (
              <button
                type="button"
                onClick={handleDownloadCV}
                className="inline-flex items-center justify-center rounded-xl bg-emerald-600 hover:bg-emerald-500 px-3.5 py-1.5 text-xs font-bold text-white transition shadow-sm"
                title="Download CV Document"
              >
                <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download CV
              </button>
            )}

            <button
              type="button"
              onClick={() => {
                if (candidate.email) {
                  window.location.href = `mailto:${candidate.email}`;
                }
              }}
              className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-3.5 py-1.5 text-xs font-bold text-slate-800 transition hover:bg-slate-50 shadow-xs"
            >
              <EmailIcons className="mr-1.5 h-3.5 w-3.5 text-slate-600" /> Email
            </button>

            <button
              type="button"
              onClick={() => onInvite?.(candidate)}
              className="inline-flex items-center justify-center rounded-xl bg-indigo-600 px-3.5 py-1.5 text-xs font-bold text-white transition hover:bg-indigo-700 shadow-xs"
            >
              <FilePlusIcons className="mr-1.5 h-3.5 w-3.5" /> Invite
            </button>

            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-white border border-slate-200 text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 ml-1"
              aria-label="Close profile dialog"
            >
              <XIcons className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Responsive Content Split Container */}
        <div className="flex-1 flex flex-col lg:flex-row overflow-hidden min-h-0">
          
          {/* Left Side: Candidate Profile Details */}
          <div
            className={`flex-1 flex-col overflow-y-auto custom-scrollbar p-4 sm:p-5 space-y-4 border-r border-slate-200 ${
              activeTab === "cv" ? "hidden lg:flex" : "flex"
            }`}
          >
            {/* Multi-Document Selector Tabs if candidate has multiple files */}
            {effectiveDocuments.length > 1 && (
              <div className="bg-slate-50 p-3 rounded-2xl border border-slate-200/80 space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2">
                    <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Attached Documents ({effectiveDocuments.length})
                  </h3>
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  {effectiveDocuments.map((doc) => {
                    const isSelected = selectedDocument?.s3Filename === doc.s3Filename;
                    return (
                      <button
                        key={doc.id || doc.s3Filename}
                        type="button"
                        onClick={() => {
                          setSelectedDocument(doc);
                          setActiveTab("cv");
                        }}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-bold border transition-all ${
                          isSelected
                            ? "bg-emerald-600 text-white border-emerald-600 shadow-xs"
                            : "bg-white text-slate-700 border-slate-200 hover:border-emerald-300"
                        }`}
                      >
                        <span className="truncate max-w-[150px]">
                          {doc.requirementLabel || doc.baseFilename || "Document"}
                        </span>
                        {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Professional Snapshot */}
            <div className="rounded-2xl bg-slate-50 p-4 border border-slate-100 shadow-xs">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-400">
                    Candidate Summary
                  </p>
                  <h2 className="mt-0.5 text-sm sm:text-base font-bold text-slate-900">
                    Professional Snapshot
                  </h2>
                </div>
                <div className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 border border-slate-200/60 shadow-xs">
                  <StarIcons className="h-3.5 w-3.5 text-amber-500" />
                  Top Talent
                </div>
              </div>
              <p className="mt-2 text-xs leading-relaxed text-slate-600">
                {getEducationSummary() ||
                  "Strong candidate with proven experience and technical skills across multiple domain projects."}
              </p>
            </div>

            {/* Skills & Expertise */}
            <div className="rounded-2xl bg-white p-4 border border-slate-200/80 shadow-xs">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-bold text-slate-900">
                    Skills & Expertise
                  </h3>
                  <p className="text-xs text-slate-500">
                    Core technical proficiencies.
                  </p>
                </div>
                <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  {candidate.skills.length} Skills
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {candidate.skills.map((skill) => (
                  <span
                    key={skill}
                    className="rounded-lg bg-slate-50 border border-slate-200/80 px-2.5 py-1 text-xs font-semibold text-slate-700"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            {/* Professional Experience */}
            <div className="rounded-2xl bg-white p-4 border border-slate-200/80 shadow-xs">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-sm font-bold text-slate-900">
                    Professional Experience
                  </h3>
                  <p className="text-xs text-slate-500">
                    Work history and roles.
                  </p>
                </div>
                <div className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-semibold text-slate-600">
                  <BuildingIcons className="h-3.5 w-3.5 text-slate-500" />
                  {candidate.work_experience?.length ?? 0} roles
                </div>
              </div>
              <div className="space-y-2.5">
                {(candidate.work_experience ?? []).map((exp, index) => (
                  <div
                    key={`${exp.job_title}-${index}`}
                    className="rounded-xl bg-slate-50/80 p-3.5 border border-slate-100"
                  >
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                      <h4 className="text-xs font-bold text-slate-900">
                        {exp.job_title}
                      </h4>
                      <span className="text-[11px] font-medium text-slate-500">
                        {exp.start_date ? formatDate(exp.start_date) : ""}
                        {exp.start_date || exp.end_date ? " — " : ""}
                        {formatDate(exp.end_date)}
                      </span>
                    </div>
                    {exp.company && (
                      <p className="text-xs font-semibold text-emerald-700 mt-0.5">
                        {exp.company}
                      </p>
                    )}
                    {exp.description && (
                      <p className="mt-1.5 text-xs leading-relaxed text-slate-600">
                        {exp.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Status History Timeline */}
            <StatusHistoryTimeline
              statusHistory={(candidate as any)?.statusHistory}
              currentStatus={candidate.status || (candidate as any)?.applicantStatus}
              createdAt={(candidate as any)?.createdAt}
              title="Status Timeline"
              collapsible={true}
            />

            {/* Contact & Candidate Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Contact Information */}
              <div className="rounded-2xl bg-white p-4 border border-slate-200/80 shadow-xs">
                <h3 className="mb-3 text-sm font-bold text-slate-900">
                  Contact Information
                </h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2.5 rounded-xl bg-slate-50 p-2 border border-slate-100">
                    <EmailIcons className="h-4 w-4 text-slate-500 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] text-slate-400 font-bold uppercase">Email</p>
                      <p className="text-xs font-semibold text-slate-900 truncate">
                        {candidate.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5 rounded-xl bg-slate-50 p-2 border border-slate-100">
                    <PhoneIcons className="h-4 w-4 text-slate-500 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] text-slate-400 font-bold uppercase">Phone</p>
                      <p className="text-xs font-semibold text-slate-900 truncate">
                        {candidate.phone ?? "No phone provided"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="rounded-2xl bg-slate-50 p-4 border border-slate-200/80 shadow-xs">
                <h3 className="mb-3 text-sm font-bold text-slate-900">
                  Candidate Stats
                </h3>
                <div className="space-y-2 text-xs">
                  <div className="rounded-xl bg-white p-2.5 border border-slate-200/60 flex items-center justify-between">
                    <span className="text-slate-500 font-medium">Match Score</span>
                    <span className="font-extrabold text-slate-900">{scorePercent == null ? '—' : `${scorePercent}%`}</span>
                  </div>
                  <div className="rounded-xl bg-white p-2.5 border border-slate-200/60 flex items-center justify-between">
                    <span className="text-slate-500 font-medium">Total Experience</span>
                    <span className="font-extrabold text-slate-900">{Math.floor(candidate.experience)} Years</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side: Uploaded CV / PDF Document Viewer Pane */}
          <div
            className={`flex-1 flex-col border-l border-slate-200 bg-slate-900 min-w-0 relative ${
              activeTab === "profile" ? "hidden lg:flex" : "flex"
            }`}
          >
            {/* Viewer Toolbar Header */}
            <div className="flex-shrink-0 flex items-center justify-between p-3.5 bg-slate-800 border-b border-slate-700 text-white">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <h3 className="text-xs font-black truncate">
                    {selectedDocument?.requirementLabel || "CV / Resume Document"}
                  </h3>
                  <p className="text-[11px] text-slate-400 truncate">
                    {selectedDocument?.baseFilename || candidate.filename?.split("/").pop() || `${candidate.name}_CV.pdf`}
                  </p>
                </div>
              </div>

              {docPreviewUrl && (
                <button
                  type="button"
                  onClick={handleDownloadCV}
                  className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold transition-all shadow-xs shrink-0 flex items-center gap-1.5"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
              )}
            </div>

            {/* Document Render Window */}
            <div className="flex-1 relative bg-slate-950 flex flex-col">
              {isDocLoading ? (
                <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                  <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mb-3" />
                  <p className="text-slate-400 text-xs font-medium animate-pulse">Loading candidate document...</p>
                </div>
              ) : docPreviewUrl ? (
                <PDFDisplay pdfUrl={docPreviewUrl} />
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-slate-400 space-y-3">
                  <div className="w-14 h-14 rounded-2xl bg-slate-800/80 flex items-center justify-center text-slate-500 border border-slate-700">
                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-300">No CV File Preview Available</p>
                    <p className="text-xs text-slate-500 max-w-xs mt-1">
                      This candidate record does not have a linked PDF or document file to display.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>,
    document.body
  );
};
